import { Color, Radius, Spacing } from 'zens-storybook'
import React, { Suspense, useEffect, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useStore, ZoneData, Draft, StorageKey, ApiError, useZoneMutation } from 'zens-store'
import { useRouter, useNumberParams } from 'zens-router'
import { useBreadcrumb } from '@dms/hook'
import { LayoutHeader, ZoneAddForm } from '@dms/component'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    margin: Spacing.L,
    padding: Spacing.L,
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
  },
})

const ZoneAdd = () => {
  const [errors, setErrors] = useState<Record<string, string>>()
  const router = useRouter()
  const store = useStore()
  const { message, storage } = store
  const [{ companyId }] = useNumberParams()
  const [data, setData] = useState<ZoneData>({
    id: 0,
    company_id: companyId,
  })
  const { t } = useTranslation()
  const mutation = useZoneMutation({ companyId })

  const breadcrumb = useBreadcrumb([
    {
      title: t('Workspaces'),
      href: `/companies/${companyId}/workspaces`,
    },
    {
      title: t('Zones'),
      href: `/companies/${companyId}/workspaces/zones`,
    },
    {
      title: t('Create new zone'),
    },
  ])

  useEffect(() => {
    const loadDefaults = async () => {
      const newData = { ...data }
      storage.get(`${companyId}:${StorageKey.Location}`, undefined, (data) => {
        const locationId = parseInt(data || '', 10)
        if (!isNaN(locationId)) newData.location_id = locationId
        setData(newData)
      })
    }
    loadDefaults()
  }, [])

  const add = async (draft: Draft<ZoneData>) => {
    try {
      setErrors({})
      const { id: zoneId } = await mutation.create(draft)
      message.add({
        message: t('Zone was created successfully'),
        status: 'success',
      })
      draft.data.location_id && storage.set(`${companyId}:${StorageKey.Location}`, `${draft.data.location_id}`)
      // @ts-expect-error Typing of boolean params in router not working correctly
      router.push({ pathname: `/companies/${companyId}/workspaces/zones/${zoneId}`, params: { workspaces: true } })
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`ZONE: Zone failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Zone could not be created')}. ${errorMessage}`,
          status: 'error',
        })
      }
    }
  }

  return (
    <View style={styles.container}>
      <LayoutHeader breadcrumb={breadcrumb} title={t('Create new zone')} />
      <View style={styles.content}>
        <Suspense fallback={<ZoneAddForm.Skeleton />}>
          <ZoneAddForm data={data} onSave={add} errors={errors} />
        </Suspense>
      </View>
    </View>
  )
}

export default ZoneAdd
